import { z } from "../zod";
import { isNull, isUndefined, isString, isNumber } from "../assertate";
import type { InferredZodSchema, ZodFormSchema } from "../types";

/**
 * @returns undefined if the baseType is not a wrapper type, or the unwrapped type if it is.
 */
function unwrapZodElement(baseType: z.ZodTypeAny): z.ZodTypeAny | undefined {
  if (baseType instanceof z.ZodEffects) {
    return baseType.sourceType();
  }
  // consider only the right side of the pipeline
  if (baseType instanceof z.ZodPipeline) {
    return baseType._def.out;
  }
  // consider only the right side of the intersection
  if (baseType instanceof z.ZodIntersection) {
    return baseType._def.right;
  }
  if (baseType instanceof z.ZodOptional) {
    return baseType.unwrap();
  }
  if (baseType instanceof z.ZodNullable) {
    return baseType.unwrap();
  }
}

export function unwrapZod(value: z.ZodTypeAny) {
  let baseType = value;
  let wrapper: z.ZodTypeAny | undefined = value;

  while (wrapper) {
    wrapper = unwrapZodElement(baseType);
    if (!wrapper) break;
    baseType = wrapper;
  }
  return baseType;
}

export function unwrapZodObject<T extends Record<string, unknown>>(
  schema: ZodFormSchema<T>,
): z.ZodObject<InferredZodSchema<T>> {
  const underlyingSchema = unwrapZod(schema);
  if (underlyingSchema instanceof z.ZodObject) {
    return underlyingSchema;
  }

  devConsole.error("Expected a ZodObject, but received:", underlyingSchema);
  throw new Error("Expected a ZodObject, but received a different type.");
}

/**
 * A way to make a factory method to do quick complex validations in a `.superRefine` method.
 *
 * @param val - The current Result object. used to return the result object if things are good.
 * @param ctx - Zod refinement context. allows us to call addIssue method.
 */
export function issueFactory<T>(val: T, ctx: z.RefinementCtx) {
  /**
   *
   * @param validator - A custom zod validator. Meant for one field.
   * @param payload - The current value of the field.
   * @param path - the path of the field. This is optional (but you probably want it.)
   */
  return function issue(validator: z.ZodTypeAny, payload: unknown, path?: string[]): T | typeof z.NEVER {
    const { success, error } = validator.safeParse(payload);
    if (!success) {
      error.errors.forEach((iss) => {
        if (path) {
          iss.path = path;
        }
        ctx.addIssue(iss);
      });
      return z.NEVER;
    }
    return val;
  };
}

export function isRequired<
  T_Validator extends z.ZodTypeAny,
  T_Value extends z.infer<T_Validator> = z.infer<T_Validator>,
>(validator: T_Validator, meetsRequirements?: (value?: T_Value | null) => boolean) {
  return validator.nullish().superRefine<z.output<T_Validator>>(function (
    value: T_Value | null | undefined,
    ctx: z.RefinementCtx,
  ): value is T_Value {
    const baseType = unwrapZod(validator);

    const isNullable = validator.isNullable();
    const isOptional = validator.isOptional();

    if (!meetsRequirements) {
      if (baseType instanceof z.ZodString) {
        meetsRequirements = (value?: string | null) => isString(value) && value.length > 0;
      } else if (baseType instanceof z.ZodNumber) {
        meetsRequirements = (value?: number | null) => isNumber(value) && value > 0;
      }
    }

    if (import.meta.env.VITE_DEBUG_ZOD) {
      devConsole.log("isRequired Base Validator", validator.description, validator, isNullable, isOptional);
    }

    if (isNullable && isNull(value)) return true;
    if (isOptional && isUndefined(value)) return true;

    if (isUndefined(value) || isNull(value) || (meetsRequirements && !meetsRequirements(value))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        params: { i18n: "errors.ruleIsRequired" },
        fatal: ctx.path.length <= 1,
      });
      return ctx.path.length > 1 ? true : z.NEVER;
    }
    return true;
  });
}

export function stripOut(validator: z.ZodTypeAny) {
  const base = unwrapZod(validator);
  base._def.toBeStripped = true;
  return validator;
}

/* Skill and training codes are validated and transformed the same way. They should be a string of 1-3 characters,
 * letters and numbers only, letters should be in all caps.
 */
export function validateSkillOrTrainCode() {
  return isRequired(
    z
      .string()
      .min(1)
      .max(3)
      .transform((val, ctx) => {
        val = val.toUpperCase();
        if (/[^A-Z0-9]+/g.test(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            i18n: "errors.code",
          });
          return z.NEVER;
        }

        return val;
      }),
  );
}

// For testing purposes, this type will help make fewer red squigglies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericZodValidationTest = { success: boolean; data?: any; errors?: any };
